import React, { useEffect, useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'

function BoldText({ text }) {
  if (!text) return null
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-slate-800">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

function HelpImage({ assetId, alt, caption, resolveAssetUrl }) {
  const [src, setSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl = null
    ;(async () => {
      try {
        const url = resolveAssetUrl(assetId)
        const r = await authFetch(url)
        if (!r.ok) throw new Error('load failed')
        const blob = await r.blob()
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setSrc(objectUrl)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [assetId, resolveAssetUrl])

  if (failed) {
    return <p className="text-xs text-slate-400 italic">Image unavailable</p>
  }
  if (!src) {
    return <div className="h-24 rounded bg-slate-100 animate-pulse" />
  }
  return (
    <figure className="space-y-1">
      <img src={src} alt={alt || ''} className="rounded-md border border-slate-200 max-w-full h-auto" />
      {caption ? <figcaption className="text-[11px] text-slate-500">{caption}</figcaption> : null}
    </figure>
  )
}

const NOTE_STYLES = {
  info: 'border-sky-200 bg-sky-50 text-sky-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
}

export default function HelpBlockRenderer({
  blocks = [],
  resolveAssetUrl = (assetId) => getLocalApiUrl(`/api/help/assets/${encodeURIComponent(assetId)}`),
  className = '',
}) {
  if (!blocks?.length) return null

  return (
    <div className={`space-y-2 ${className}`}>
      {blocks.map((block, idx) => {
        if (!block || !block.type) return null
        switch (block.type) {
          case 'heading': {
            const level = Math.min(4, Math.max(2, Number(block.level) || 3))
            const Tag = `h${level}`
            const size =
              level === 2 ? 'text-sm font-bold' : level === 3 ? 'text-sm font-semibold' : 'text-xs font-semibold'
            return (
              <Tag key={idx} className={`${size} text-slate-900`}>
                {block.text}
              </Tag>
            )
          }
          case 'paragraph':
            return (
              <p key={idx} className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                <BoldText text={block.text} />
              </p>
            )
          case 'bullets':
            return (
              <ul key={idx} className="text-sm text-slate-600 leading-relaxed list-disc pl-4 space-y-1">
                {(block.items || []).map((item, i) => (
                  <li key={i}>
                    <BoldText text={item} />
                  </li>
                ))}
              </ul>
            )
          case 'note':
            return (
              <div
                key={idx}
                className={`rounded-md border px-3 py-2 text-xs leading-relaxed ${
                  NOTE_STYLES[block.variant] || NOTE_STYLES.info
                }`}
              >
                <BoldText text={block.text} />
              </div>
            )
          case 'image':
            return (
              <HelpImage
                key={idx}
                assetId={block.asset_id}
                alt={block.alt}
                caption={block.caption}
                resolveAssetUrl={resolveAssetUrl}
              />
            )
          case 'legal_footer':
            return null
          default:
            return null
        }
      })}
    </div>
  )
}
